import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, KeyRound, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal shown automatically when an authenticated user has
 * `force_password_change === true` on their JWT user record.
 *
 * Blocks ALL navigation — the only way out is to set a new password.
 * Mounted once inside MainLayout so it lives across page navigation.
 */
export function ForcePasswordChangeModal() {
  const { user, refreshUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!user?.force_password_change) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    if (next === current) { setError('New password must be different from current'); return; }
    setSubmitting(true);
    try {
      await authAPI.changePassword(current, next);
      toast.success('Password updated. You can keep using the app.');
      if (refreshUser) await refreshUser();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open modal>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="force-password-change-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            Change your password to continue
          </DialogTitle>
          <DialogDescription>
            An administrator reset your password to a temporary one. Please choose a new password to keep using the platform.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="space-y-2">
            <Label>Temporary password</Label>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)}
                   required data-testid="fpc-current" autoFocus />
          </div>
          <div className="space-y-2">
            <Label>New password (min. 8 chars)</Label>
            <PasswordInput value={next} onChange={(e) => setNext(e.target.value)}
                   required minLength={8} data-testid="fpc-new" />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   required minLength={8} data-testid="fpc-confirm" />
          </div>
          <Button type="submit" className="w-full" disabled={submitting} data-testid="fpc-submit">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</> : <><KeyRound className="h-4 w-4 mr-2" />Change password</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
